<template>
  <MuiForm
    v-model="formData"
    class="SignUpEmployeeStep1View Step__form"
    @submit.native="submit"
  >
    <h1 class="font-normal mb-5 h1">
      {{ $t('Views.user_signup.step_1.title') }}
    </h1>
    <MuiInputText
      v-model="formModel.email"
      :label="$t('Views.user_signup.fields.email')"
      :placeholder="$t('Views.user_signup.fields.email_placeholder')"
      :disabled="isInvitation"
      required
      full-width
      type="email"
      name="email"
    />
    <MuiInputText
      v-model="formModel.password"
      :label="$t('Views.user_signup.fields.password')"
      :placeholder="$t('Views.user_signup.fields.password')"
      :on-blur="() => { $segments.trackSegment('Email Password Added', { email: data.email })}"
      :pattern="[
        { rule: /\w*[A-Z]\w*/, message: $t('Views.user_signup.step_1.password.password_uppercase') },
        { rule: /\w*[0-9]\w*/, message: $t('Views.user_signup.step_1.password.password_number') },
        { rule: /^.{6,}$/, message: $t('Views.user_signup.step_1.password.password_length') },
        { rule: /^[a-zA-Z0-9]*$/, message: $t('Views.user_signup.step_1.password.no_characters') },
      ]"
      :custom-error-messages="{
        empty: $t('Views.user_signup.step_1.errors.mandatory'),
      }"
      required
      full-width
      type="password"
      name="password"
    />
    <div class="password-label font-xs">
      {{ $t('Views.user_signup.fields.password_text') }}
    </div>
    <MuiButtonFlat
      submit
      class="Step__form__next-button mb-4 cursor-pointer"
    >
      {{ $t('Views.user_signup.step_1.button') }}
    </MuiButtonFlat>
    <span v-if="!isInvitation">
      <span class="color-ink-light font-m">{{ $t('Views.user_signup.step_1.are_you_company') }}</span>
      <RouterLink
        :to="{ name:'SignUp_Company-Step-1' }"
        class="font-m font-semibold"
      >{{ $t('Views.user_signup.step_1.are_you_company_click') }}
      </RouterLink>
    </span>
  </MuiForm>
</template>

<script>
import {
  mapMutations,
  mapState,
  mapActions,
} from 'vuex';
import _ from 'lodash';
import {
  MuiButtonFlat,
  MuiInputText,
  MuiForm,
  ModalSizes,
} from '@ubeeqo/motion-ui';
import { EMPLOYEE_MODULE } from '@/constants/modules';

import { steps } from '../constants';
// import B2BModalComponent from './B2BModalComponent';

const b2bModalId = 'B2BModal';

export default {
  name: 'SignUpEmployeeStep1View',
  components: {
    MuiInputText,
    MuiButtonFlat,
    MuiForm,
  },
  data() {
    return {
      formData: {},
    };
  },
  computed: {
    ...mapState(EMPLOYEE_MODULE, {
      data: state => state.data,
      done: state => state.done,
      b2b: state => state.b2b,
      STATUS: state => state.STATUS,
      isValidInvite: state => state.b2b.invitation.valid_invite,
    }),
    isInvitation() {
      return this.isQueryInvitation && this.isValidInvite;
    },
    isQueryInvitation() {
      return this.$route.params.invitationToken && this.$route.params.invitationUuid;
    },
    formModel() {
      const { email, password } = _.get(this, 'data', {});
      return {
        email,
        password,
      };
    },
  },
  watch: {
    formData(value) {
      const stepStatus = this.done[steps.step1];

      if (value.valid && !stepStatus) {
        this.doneStep(steps.step1);
      } else if (!value.valid && stepStatus) {
        this.unDoneStep(steps.step1);
      }
    },
  },
  mounted() {
    this.updateStep(steps.step1);
  },
  created() {
    if (this.$route.params.promo) {
      this.setPromoCode(this.$route.params.promo);
    }

    if (this.isQueryInvitation) {
      this.checkEmployeeInvitation();
    }
  },
  methods: {
    ...mapMutations(EMPLOYEE_MODULE, [
      'country',
      'doneStep',
      'unDoneStep',
      'updateStep',
      'isEmployee',
      'setInvitationCompany',
      'setPromoCode',
      'setData',
    ]),
    ...mapActions(EMPLOYEE_MODULE, [
      'checkExistingEmail',
      'checkInvitation',
    ]),
    async submit() {
      if (!this.formData.valid) return;

      this.setData(this.formModel);

      await this.checkExistingEmail();

      if (this.STATUS.LOADED) {
        this.checkExistingEmailOnSuccess();
      } else {
        this.checkExistingEmailOnError();
      }
    },
    createWarningNotification(text) {
      this.$notifications.create({
        type: 'warning',
        delayToClose: false,
        text,
        textAction: 'Login',
        onClose: () => this.$router.push('/'),
      });
    },
    loadB2BModal(company) {
      const getTranslation = field => `Views.user_signup.step_1.b2b.${field}`;

      this.$modal.show(b2bModalId, {
        size: ModalSizes.small,
        title: this.$t(getTranslation('title')),
        closeButton: false,
        footerAlign: 'right',
        content: {
          // component: B2BModalComponent,
          props: {
            companyName: company.commercial_name,
          },
        },
        acceptButton: {
          type: 'main',
          text: this.$t(getTranslation('accept_button')),
          action: () => {
            this.isEmployee(true);
            this.setInvitationCompany({ ...company, cs_operator_uuid: company.cs_operator });
            this.$modal.close(b2bModalId);
            this.nextStep();
          },
        },
        cancelButton: {
          type: 'default',
          text: this.$t(getTranslation('cancel_button')),
          action: () => {
            this.setInvitationCompany(false);
            this.$modal.close(b2bModalId);
          },
        },
      });
    },
    async checkEmployeeInvitation() {
      const { invitationToken, invitationUuid } = this.$route.params;
      await this.checkInvitation({ invitationUuid, invitationToken });

      if (this.STATUS.ERROR_INVITATION) {
        this.createWarningNotification(this.$t('Views.user_signup.step_1.errors.mail_error'));
      }
    },

    checkExistingEmailOnSuccess() {
      if (this.b2b.email_from_company) {
        this.loadB2BModal(this.b2b.invitation.company);
      } else {
        this.nextStep();
      }
    },
    checkExistingEmailOnError() {
      this.formData.valid = false;
      const errorMessage = this.$t('Views.user_signup.step_1.errors.mail_error');
      this.createWarningNotification(errorMessage);
    },
    nextStep() {
      this.doneStep(steps.step1);
      this.$router.push('./step2');
    },
  },
};
</script>
